/**
 * Lista de Event Types do módulo de Agendamento Online
 * 
 * Exibe cards com informações resumidas de cada tipo de evento,
 * permitindo ativar/desativar, editar e excluir.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import { stripTemplateVariables } from '@/lib/schedulingUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteEventTypeDialog } from './DeleteEventTypeDialog';
import {
  Clock,
  Copy,
  ExternalLink,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Video,
  Calendar,
} from 'lucide-react';
import { type SchedulingEventType, useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface SchedulingEventTypesListProps {
  /** Callback ao clicar em editar um event type */
  onEdit: (eventType: SchedulingEventType) => void;
  /** Callback ao clicar em criar novo */
  onCreate: () => void;
}

export function SchedulingEventTypesList({ onEdit, onCreate }: SchedulingEventTypesListProps) {
  const { eventTypes, isLoading, toggleActive, deleteEventType, isDeleting } = useSchedulingEventTypes();
  const { currentTenant } = useAuth();
  const [deleteTarget, setDeleteTarget] = useState<SchedulingEventType | null>(null);

  /** Copia o link público do agendamento para a clipboard (inclui slug do tenant) */
  const handleCopyLink = (slug: string) => {
    const tenantSlug = currentTenant?.slug || 'empresa';
    const url = `${window.location.origin}/schedule/${tenantSlug}/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  // Estado de carregamento
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="border-border/50">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Estado vazio
  if (eventTypes.length === 0) {
    return (
      <Card className="border-dashed border-2 border-border/50">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
            <Calendar className="h-8 w-8" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Nenhum tipo de evento
          </h3>
          <p className="text-muted-foreground mb-6 max-w-sm">
            Crie seu primeiro tipo de evento para que clientes possam agendar reuniões com você.
          </p>
          <Button onClick={onCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Criar Tipo de Evento
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {eventTypes.map((et) => (
          <Card
            key={et.id}
            className="group relative overflow-hidden border-border/50 transition-all hover:border-border hover:shadow-md cursor-pointer"
            onClick={() => onEdit(et)}
          >
            {/* Barra colorida no topo */}
            <div
              className="h-1.5 w-full"
              style={{ backgroundColor: et.color || '#3B82F6' }}
            />

            <CardContent className="p-5 space-y-4">
              {/* Header: Título + Menu */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-foreground truncate">{stripTemplateVariables(et.title)}</h3>
                  {et.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {et.description}
                    </p>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(et); }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCopyLink(et.slug); }}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar Link
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(et); }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Badges informativos */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="gap-1 text-xs">
                  <Clock className="h-3 w-3" />
                  {et.duration_minutes} min
                </Badge>
                {et.conferencing_provider === 'google_meet' && (
                  <Badge variant="outline" className="gap-1 text-xs">
                    <Video className="h-3 w-3" />
                    Google Meet
                  </Badge>
                )}
                {et.requires_approval && (
                  <Badge variant="outline" className="gap-1 text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                    Aprovação
                  </Badge>
                )}
              </div>

              {/* Footer: Toggle ativo + slug */}
              <div className="flex items-center justify-between pt-2 border-t border-border/50">
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={et.is_active}
                    onCheckedChange={(checked) => toggleActive({ id: et.id, is_active: checked })}
                    className="scale-90"
                  />
                  <span className="text-xs text-muted-foreground">
                    {et.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <code className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                  /{et.slug}
                </code>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Card para adicionar novo */}
        <Card
          className="border-dashed border-2 border-border/50 hover:border-primary/50 transition-colors cursor-pointer group"
          onClick={onCreate}
        >
          <CardContent className="flex flex-col items-center justify-center h-full min-h-[200px] text-muted-foreground group-hover:text-primary transition-colors">
            <Plus className="h-8 w-8 mb-2" />
            <span className="text-sm font-medium">Novo Tipo de Evento</span>
          </CardContent>
        </Card>
      </div>

      {/* Modal de exclusão segura com confirmação por frase */}
      <DeleteEventTypeDialog
        eventType={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteEventType}
        isDeleting={isDeleting}
      />
    </>
  );
}
